import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertTriangle, CheckCircle2, XCircle, Loader2, RefreshCw,
  ShieldCheck, FileText, Ban, Wand2, Download, Target,
} from 'lucide-react';
import PressPdfStatusChip from './PressPdfStatusChip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const t = localStorage.getItem('mosaic_session_token') || '';
  return { headers: { Authorization: `Bearer ${t}` } };
};

const statusStyle = (s) => {
  switch (s) {
    case 'committed': return 'bg-emerald-100 text-emerald-800 border-emerald-300';
    case 'rejected':  return 'bg-gray-200 text-gray-700 border-gray-300';
    case 'error':     return 'bg-rose-100 text-rose-800 border-rose-300';
    default:          return 'bg-amber-100 text-amber-800 border-amber-300';
  }
};

const BPVisualFixBatchPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState('');
  const [selectedOnly, setSelectedOnly] = useState(null);
  const [retryingKey, setRetryingKey] = useState(null);
  // v6.32.15: batch regen state
  const [regenPwOpen, setRegenPwOpen] = useState(false);
  const [regenPw, setRegenPw] = useState('');
  const [regenStatus, setRegenStatus] = useState(null);
  const [regenStarting, setRegenStarting] = useState(false);
  // v6.32.16: cancel-run state
  const [cancelPwOpen, setCancelPwOpen] = useState(false);
  const [cancelPw, setCancelPw] = useState('');
  const [cancelling, setCancelling] = useState(false);
  // v6.35.34: per-scene dismiss-error in flight tracker + hide-committed toggle
  const [dismissingKey, setDismissingKey] = useState(null);
  const [hideCommitted, setHideCommitted] = useState(true);
  // v6.35.35: daily LLM spend snapshot (refreshed every 30s while panel
  // is open). Used to warn operators BEFORE they hit the cap.
  const [llmSpend, setLlmSpend] = useState(null);

  // Determine if the current running state looks stuck.
  // v6.32.18: tightened from 5 min → 90s, matching backend. With a 30s
  // heartbeat loop and 150s per-scene timeout, a healthy worker pings
  // at most every 30s; 90s without a tick means the worker is dead.
  const regenIsStuck = (() => {
    if (regenStatus?.status !== 'running') return false;
    const hb = regenStatus.last_heartbeat_dt || regenStatus.started_at_dt;
    if (!hb) return false;
    const hbMs = new Date(hb).getTime();
    return Date.now() - hbMs > 90 * 1000;
  })();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/bp-auditor/visual-fix/review`, authCfg());
      setData(data);
    } catch (e) {
      toast.error(`Load visual-fix review failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // v6.35.35: poll daily LLM spend every 30s so the operator sees the
  // bucket fill up live during a regen and can stop before hitting the
  // cap. Endpoint reads from MongoDB so the value is shared across
  // all browser tabs / all backend pods.
  useEffect(() => {
    let timer = null;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/bp-auditor/diagnostics/llm-spend-today`, authCfg());
        setLlmSpend(data);
      } catch (_e) {
        // silent — keep showing previous value
      }
      timer = setTimeout(tick, 30000);
    };
    tick();
    return () => { if (timer) clearTimeout(timer); };
  }, []);

  // v6.32.15: poll regen-status every 5s while running
  useEffect(() => {
    let timer = null;
    const poll = async () => {
      try {
        const { data: st } = await axios.get(`${API}/bp-auditor/visual-fix/regen-status`, authCfg());
        setRegenStatus(st);
        if (st.status === 'running') {
          timer = setTimeout(poll, 5000);
        } else if (st.status === 'complete') {
          toast.success(`Regen complete: ${st.ok || 0} candidates generated, ${st.fail || 0} failed.`);
          load();  // refresh review data
        } else if (st.status === 'failed') {
          toast.error(`Regen failed: ${(st.error || '').slice(0, 200)}`);
        }
      } catch (_e) {
        // Silent — likely unauth or network blip; try again
        timer = setTimeout(poll, 10000);
      }
    };
    poll();
    return () => { if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const startRegen = async () => {
    if (!regenPw) { toast.error('Password required'); return; }
    setRegenStarting(true);
    try {
      // v6.32.16: if we think it's stuck, pass force=true so the endpoint
      // replaces the stuck status doc instead of returning 409.
      await axios.post(`${API}/bp-auditor/visual-fix/regen-all`,
        { password: regenPw, force: regenIsStuck }, authCfg());
      toast.success('Regeneration started (~8-12 min for 40-90 scenes). Progress polls every 5s.');
      setRegenPwOpen(false);
      setRegenPw('');
      try {
        const { data: st } = await axios.get(`${API}/bp-auditor/visual-fix/regen-status`, authCfg());
        setRegenStatus(st);
      } catch (_e) { /* ignore */ }
    } catch (e) {
      // v6.35.35 — surface daily-LLM-cap 429 as a friendly amber toast.
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 429 && detail?.error === 'daily_llm_cap_reached') {
        toast.error(
          `Daily content-engine cap reached ($${detail.today_usd.toFixed(2)} / $${detail.cap_usd.toFixed(2)}). ` +
          `Wait until 00:00 UTC for the bucket to roll over, or bump MOSAIC_DAILY_LLM_CAP_USD via Emergent vault.`,
          { duration: 12000 }
        );
      } else {
        toast.error(`Regen start failed: ${e?.response?.data?.detail || e.message}`);
      }
    } finally {
      setRegenStarting(false);
    }
  };

  // v6.32.16: cancel current run
  const cancelRun = async () => {
    if (!cancelPw) { toast.error('Password required'); return; }
    setCancelling(true);
    try {
      const { data } = await axios.post(`${API}/bp-auditor/visual-fix/cancel-run`,
        { password: cancelPw }, authCfg());
      if (data.cancelled > 0) {
        toast.success('Run cancelled. You can click Regenerate all to start fresh.');
      } else {
        toast.info('No running job to cancel. Status already cleared.');
      }
      setCancelPwOpen(false);
      setCancelPw('');
      try {
        const { data: st } = await axios.get(`${API}/bp-auditor/visual-fix/regen-status`, authCfg());
        setRegenStatus(st);
      } catch (_e) { /* ignore */ }
    } catch (e) {
      toast.error(`Cancel failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setCancelling(false);
    }
  };

  const rejectScene = async (s) => {
    if (!window.confirm(`Reject the visual-fix candidate for "${s.context}" (Grade ${s.grade})? The current image stays in place.`)) return;
    try {
      await axios.post(`${API}/bp-auditor/visual-fix/reject`, {
        grade: s.grade, context: s.context,
      }, authCfg());
      toast.success(`Rejected: ${s.context}`);
      await load();
    } catch (e) {
      toast.error(`Reject failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const unrejectScene = async (s) => {
    try {
      await axios.post(`${API}/bp-auditor/visual-fix/unreject`, {
        grade: s.grade, context: s.context,
      }, authCfg());
      toast.success(`Restored to pending: ${s.context}`);
      await load();
    } catch (e) {
      toast.error(`Restore failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const retryScene = async (s) => {
    const key = `${s.grade}|${s.context}`;
    setRetryingKey(key);
    try {
      const { data } = await axios.post(`${API}/bp-auditor/visual-fix/retry`, {
        grade: s.grade, context: s.context,
      }, { ...authCfg(), timeout: 90000 });
      toast.success(`Retry succeeded — new candidate ready (${Math.round((data.bytes || 0) / 1024)} KB)`);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(`Retry failed: ${detail}`, { duration: 8000 });
    } finally {
      setRetryingKey(null);
    }
  };

  // v6.35.34: dismiss a stale/historical error string (e.g. LiteLLM
  // budget-cap message from before the direct-LLM cutover) so the card
  // leaves the error bucket without re-running a full batch regen.
  const dismissError = async (s) => {
    const key = `${s.grade}|${s.context}`;
    if (!window.confirm(
      `Dismiss the historical generation error for "${s.context}" (Grade ${s.grade})?\n\n` +
      `This nulls the stored error string and the empty candidate URL so the card ` +
      `leaves the Errors bucket. The live student-facing image is NOT touched. ` +
      `Audit-logged. Reversible only by re-running a regen.`
    )) return;
    setDismissingKey(key);
    try {
      await axios.post(`${API}/bp-auditor/visual-fix/dismiss-error`, {
        grade: s.grade, context: s.context,
      }, authCfg());
      toast.success(`Dismissed historical error: ${s.context}`);
      await load();
    } catch (e) {
      toast.error(`Dismiss failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setDismissingKey(null);
    }
  };

  const downloadCertificate = async () => {
    const t = toast.loading('Generating visual-anchor certificate…', { duration: Infinity });
    try {
      const res = await axios.get(`${API}/bp-auditor/visual-fix/certificate`, {
        ...authCfg(), responseType: 'blob', timeout: 60000,
      });
      toast.dismiss(t);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `MOSAIC_WIDA_Visual_Anchor_Certificate_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Certificate downloaded.');
    } catch (e) {
      toast.dismiss(t);
      let msg = e.message;
      try {
        const text = await e.response?.data?.text?.();
        if (text) { try { msg = JSON.parse(text).detail || text; } catch { msg = text; } }
      } catch {}
      toast.error(`Certificate failed: ${msg}`);
    }
  };

  const openApproveAll = (scope) => {
    setSelectedOnly(scope);
    setPw('');
    setPwOpen(true);
  };

  const confirmApprove = async () => {
    if (!pw) { toast.error('Password required'); return; }
    setApproving(true);
    setPwOpen(false);
    const commitToast = toast.loading(
      selectedOnly
        ? `Committing ${selectedOnly.length} scene(s)…`
        : `Committing ${counts.pending || 0} scene(s)… This can take 20-40 seconds.`,
      { duration: Infinity }
    );
    try {
      const body = { password: pw };
      if (selectedOnly) body.only = selectedOnly;
      const { data } = await axios.post(
        `${API}/bp-auditor/visual-fix/approve-all`, body, { ...authCfg(), timeout: 180000 }
      );
      toast.dismiss(commitToast);
      toast.success(
        `Committed ${data.counts.committed} · Skipped ${data.counts.skipped} · Errors ${data.counts.errors}`,
        { duration: 6000 }
      );
      if (data.press_regen_triggered) {
        toast.info(
          '112 press-PDFs regenerating in the background (~2-3 min). Poll via Content Auditor → Regen Press PDFs status panel.',
          { duration: 10000 }
        );
      }
      setPw('');
      await load();
    } catch (e) {
      toast.dismiss(commitToast);
      toast.error(`Approve failed: ${e?.response?.data?.detail || e.message}`);
      setPwOpen(true);
    } finally {
      setApproving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border rounded-xl p-6 text-sm text-gray-500">
        <Loader2 className="inline-block animate-spin mr-2" size={14} /> Checking for batch visual-anchor fixes…
      </div>
    );
  }

  if (!data?.exists) {
    const regenRunning = regenStatus?.status === 'running';
    return (
      <div className="bg-white border-2 border-purple-300 rounded-xl p-6 space-y-4" data-testid="bp-visual-fix-panel-empty">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-purple-100 text-purple-700 flex items-center justify-center rounded-lg flex-shrink-0">
            <Wand2 size={22} />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold">Batch Visual-Anchor Fix — one complete run</h3>
            <p className="text-sm text-gray-700 mt-1 max-w-3xl">
              No visual-fix review payload is loaded yet. Click below to regenerate every failing Big Picture candidate for the latest audit run. Each candidate is stored in MongoDB so it renders correctly across all backend replicas on production. Takes ~8-12 min for 40 scenes.
            </p>
            {regenRunning && (
              <div className="mt-3 bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-indigo-900">
                  <Loader2 className="animate-spin" size={16} />
                  Regenerating candidates — {regenStatus?.progress?.done || 0} / {regenStatus?.progress?.total || '?'} done
                </div>
                <div className="mt-2 h-1.5 bg-indigo-100 rounded overflow-hidden">
                  <div className="h-full bg-indigo-500 transition-all"
                       style={{ width: `${regenStatus?.progress?.total ? (regenStatus.progress.done / regenStatus.progress.total * 100) : 5}%` }} />
                </div>
              </div>
            )}
            <Button
              onClick={() => setRegenPwOpen(true)}
              disabled={regenRunning}
              className="mt-4 bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="bp-visual-fix-regen-start"
            >
              <Wand2 size={14} className="mr-1" />
              Regenerate all candidates
            </Button>
          </div>
        </div>
        <Dialog open={regenPwOpen} onOpenChange={(v) => !v && setRegenPwOpen(false)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Regenerate all visual-fix candidates</DialogTitle>
              <DialogDescription>
                Generates a fresh candidate for every scene that overall-failed
                without text violations. ~$1-2 in content-engine cost, ~8-12 min runtime.
                Runs in the background; the panel auto-refreshes when done.
              </DialogDescription>
            </DialogHeader>
            <Input
              type="password"
              placeholder="Super Admin password"
              value={regenPw}
              onChange={e => setRegenPw(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !regenStarting && startRegen()}
              autoFocus
              data-testid="bp-visual-fix-regen-password"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setRegenPwOpen(false)} disabled={regenStarting}>
                Cancel
              </Button>
              <Button
                onClick={startRegen}
                disabled={regenStarting || !regenPw}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="bp-visual-fix-regen-confirm"
              >
                {regenStarting
                  ? <><Loader2 size={14} className="mr-1 animate-spin" />Starting…</>
                  : 'Start regeneration'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  const counts = data.counts || {};
  const scenes = data.scenes || [];

  return (
    <div className="bg-white border border-indigo-200 rounded-xl p-6 space-y-4" data-testid="bp-visual-fix-panel">
      {/* v6.35.35: daily content-engine spend banner — surfaces today's bucket and
          warns at 75%/95% before a regen would hit 429. Driven by the
          new /api/bp-auditor/diagnostics/llm-spend-today endpoint;
          poll every 30s while the panel is open. */}
      {llmSpend && (() => {
        const pct = llmSpend.pct_used || 0;
        const atCap = pct >= 100;
        const danger = pct >= 95;
        const warn = pct >= 75;
        const tone = atCap
          ? { bg: 'bg-rose-50', border: 'border-rose-300', text: 'text-rose-900', bar: 'bg-rose-500' }
          : danger
          ? { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-900', bar: 'bg-amber-500' }
          : warn
          ? { bg: 'bg-yellow-50', border: 'border-yellow-300', text: 'text-yellow-900', bar: 'bg-yellow-400' }
          : { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-900', bar: 'bg-emerald-500' };
        return (
          <div
            className={`${tone.bg} border ${tone.border} rounded-lg p-3`}
            data-testid="bp-visual-fix-llm-spend-banner"
          >
            <div className={`flex items-center justify-between text-xs font-semibold ${tone.text}`}>
              <span>
                Daily content-engine spend ({llmSpend.date}):{' '}
                <span data-testid="bp-visual-fix-llm-spend-amount">
                  ${(llmSpend.total_usd || 0).toFixed(2)}
                </span>
                {' '} / ${(llmSpend.cap_usd || 0).toFixed(2)}
                {' '}({pct.toFixed(0)}%) — {llmSpend.total_calls || 0} calls
              </span>
              {atCap && <span className="ml-2">CAP REACHED — regens will return 429</span>}
              {!atCap && danger && <span className="ml-2">Approaching cap</span>}
            </div>
            <div className="mt-2 h-2 w-full bg-white/70 rounded overflow-hidden">
              <div
                className={`h-full ${tone.bar} transition-all duration-300`}
                style={{ width: `${Math.min(100, pct)}%` }}
              />
            </div>
            {atCap && (
              <div className="mt-2 text-xs text-rose-900">
                Wait until 00:00 UTC for the daily bucket to roll over, OR
                bump <code className="bg-white/80 px-1 rounded">MOSAIC_DAILY_LLM_CAP_USD</code>
                {' '}via Emergent vault and redeploy.
              </div>
            )}
          </div>
        );
      })()}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Target size={20} className="text-indigo-600" />
            <h3 className="text-lg font-bold">Batch Visual-Anchor Fix (WIDA Content-Fit)</h3>
            <Badge className="bg-indigo-100 text-indigo-800 border-indigo-300 border" variant="outline">
              {counts.total || 0} scenes flagged
            </Badge>
          </div>
          <p className="text-xs text-gray-600 max-w-3xl">
            MOSAIC content engine Vision flagged these Big Picture scenes for failing to visually depict every
            required correct-answer anchor — students cannot solve listening questions from the image alone.
            MOSAIC image engine has generated replacement candidates that explicitly render every missing
            anchor. Review side-by-side below, reject any you don't want, then bulk-commit the rest.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <PressPdfStatusChip />
          <Button variant="outline" size="sm" onClick={load} data-testid="bp-visual-fix-refresh">
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setRegenPwOpen(true)}
            disabled={regenStatus?.status === 'running'}
            className="border-purple-300 text-purple-700 hover:bg-purple-50"
            data-testid="bp-visual-fix-regen-all"
          >
            <Wand2 size={14} className="mr-1" />
            {/* v6.32.17: prefer prospective count from latest audit run */}
            Regenerate all ({data?.prospective?.scene_count ?? counts.total ?? 0})
          </Button>
          {(counts.committed || 0) > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={downloadCertificate}
              className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
              data-testid="bp-visual-fix-download-cert"
            >
              <Download size={14} className="mr-1" />
              Download Certificate ({counts.committed || 0})
            </Button>
          )}
          <Button
            onClick={() => openApproveAll(null)}
            disabled={!counts.pending}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="bp-visual-fix-approve-all"
          >
            <ShieldCheck size={14} className="mr-1" />
            Approve All Pending ({counts.pending || 0})
          </Button>
        </div>
      </div>

      {/* counts strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
        {[
          { k: 'Total',     v: counts.total || 0,     c: 'text-gray-900' },
          { k: 'Pending',   v: counts.pending || 0,   c: 'text-amber-700' },
          { k: 'Committed', v: counts.committed || 0, c: 'text-emerald-700' },
          { k: 'Rejected',  v: counts.rejected || 0,  c: 'text-gray-700' },
          { k: 'Errors',    v: counts.error || 0,     c: 'text-rose-700' },
        ].map(s => (
          <div key={s.k} className="bg-gray-50 border rounded-md p-2 text-center">
            <div className="uppercase text-[10px] tracking-wide text-gray-500">{s.k}</div>
            <div className={`text-lg font-semibold ${s.c}`}>{s.v}</div>
          </div>
        ))}
      </div>

      {data.generated_at && (
        <div className="text-[11px] text-gray-500 flex items-center gap-1">
          <FileText size={11} /> Batch generated: {data.generated_at.slice(0, 19).replace('T', ' ')} UTC
          {data.run_id && <> · from audit run <code className="text-gray-600">{data.run_id}</code></>}
        </div>
      )}

      {/* v6.32.17: stale-review notice when a fresher audit exists */}
      {data?.prospective?.is_stale && data.prospective.scene_count !== counts.total && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm" data-testid="bp-visual-fix-stale-notice">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-amber-900">A fresher BP audit is available</div>
              <div className="text-xs text-amber-800 mt-0.5">
                This review payload was generated from <code>{data.run_id}</code> with {counts.total} scenes.
                The latest complete audit (<code>{data.prospective.run_id}</code>) has <strong>{data.prospective.scene_count} scenes</strong> failing visual-anchor checks.
                Clicking <strong>Regenerate all ({data.prospective.scene_count})</strong> will regenerate against the newer audit.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* v6.32.15: regen progress banner when a bg regen is running */}
      {regenStatus?.status === 'running' && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3" data-testid="bp-visual-fix-regen-banner">
          <div className="flex items-center gap-2 text-sm font-semibold text-indigo-900">
            <Loader2 className="animate-spin" size={16} />
            Regenerating all candidates — {regenStatus?.progress?.done || 0} / {regenStatus?.progress?.total || '?'} done
          </div>
          <div className="mt-2 h-1.5 bg-indigo-100 rounded overflow-hidden">
            <div className="h-full bg-indigo-500 transition-all"
                 style={{ width: `${regenStatus?.progress?.total ? (regenStatus.progress.done / regenStatus.progress.total * 100) : 5}%` }} />
          </div>
          <div className="text-xs text-indigo-700 mt-1 flex items-center justify-between gap-2 flex-wrap">
            <span>Panel auto-refreshes when complete. You can leave this tab.</span>
            {/* v6.32.16: cancel button always visible; red-outlined if stuck (no heartbeat > 5 min) */}
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCancelPwOpen(true)}
              className={regenIsStuck ? "border-rose-400 text-rose-700 hover:bg-rose-50" : "border-indigo-300 text-indigo-700 hover:bg-indigo-100"}
              data-testid="bp-visual-fix-cancel-run"
            >
              <XCircle size={12} className="mr-1" />
              {regenIsStuck ? "Cancel (appears stuck)" : "Cancel run"}
            </Button>
          </div>
          {regenIsStuck && (
            <div className="mt-2 text-xs text-rose-700 font-medium bg-rose-50 border border-rose-200 rounded p-2">
              No heartbeat in &gt; 90 seconds. The worker was likely killed by a pod restart or got stuck on a hung MOSAIC engine call.
              Cancel this run, then click <strong>Regenerate all</strong> again — the previous candidates (if any) are preserved in MongoDB so you only re-pay for what wasn't done.
            </div>
          )}
        </div>
      )}

      {/* v6.32.25: visible failure banner — surfaces budget-exceeded /
          worker-crash / fatal errors that previously had no visual indicator
          (status flips from running → failed within seconds, so the
          running-state banner above never renders). The user reported
          clicking Regenerate all and seeing nothing happen because every
          MOSAIC engine + MOSAIC image engine call was being rejected at the API layer due
          to the Emergent content-engine Key budget cap. With this banner, the failure
          + the actual error message are now front-and-center.

          v6.35.34: when `fail === 0` (i.e. nothing actually broke — the run
          just short-circuited or cancelled while still leaving the status
          doc at `failed`), render the banner as informational green
          "Regen complete" instead of misleading rose "Regen failed". The
          old wording confused operators ("Regen failed – 14 succeeded · 0
          failed (of 110 total)" looked like a partial failure when nothing
          had actually failed). */}
      {regenStatus?.status === 'failed' && (() => {
        const ok = regenStatus?.ok || 0;
        const fail = regenStatus?.fail || 0;
        const total = regenStatus?.progress?.total || '?';
        const hadActualFailures = fail > 0;
        const surface = hadActualFailures
          ? { bg: 'bg-rose-50', border: 'border-rose-300', heading: 'text-rose-900', icon: 'text-rose-600' }
          : { bg: 'bg-emerald-50', border: 'border-emerald-300', heading: 'text-emerald-900', icon: 'text-emerald-600' };
        return (
          <div
            className={`${surface.bg} border ${surface.border} rounded-lg p-3`}
            data-testid="bp-visual-fix-regen-failed-banner"
          >
            <div className={`flex items-center gap-2 text-sm font-semibold ${surface.heading}`}>
              {hadActualFailures
                ? <XCircle size={16} className={surface.icon} />
                : <CheckCircle2 size={16} className={surface.icon} />}
              {hadActualFailures
                ? <>Regen failed — {ok} succeeded · {fail} failed (of {total} total)</>
                : <>Regen complete — {ok} regenerated{typeof total === 'number' && total > ok ? `, ${total - ok} already current` : ''}</>}
            </div>
            {regenStatus?.error && hadActualFailures && (
              <div className="mt-2 text-xs text-rose-800 bg-white/60 border border-rose-200 rounded p-2 font-mono break-words">
                {regenStatus.error}
              </div>
            )}
            {/^.*budget.*exceed/i.test(regenStatus?.error || '') && hadActualFailures && (
              <div className="mt-2 text-xs text-rose-900 bg-amber-50 border border-amber-300 rounded p-2">
                <strong>Budget cap hit.</strong> The Emergent Universal Key has
                run out of credit. To unblock: open Emergent platform →{' '}
                <strong>Profile → Universal Key → Add Balance</strong>. Optionally
                enable <strong>Auto top-up</strong> so this doesn't happen
                mid-batch again. After topping up, click Regenerate all to retry
                — already-generated candidates are preserved in MongoDB so you
                only re-pay for the scenes that haven't completed.
              </div>
            )}
            <div className="mt-2 flex justify-end">
              <Button
                size="sm"
                variant="outline"
                onClick={load}
                className={hadActualFailures
                  ? "border-rose-300 text-rose-700 hover:bg-rose-100"
                  : "border-emerald-300 text-emerald-700 hover:bg-emerald-100"}
                data-testid="bp-visual-fix-regen-failed-dismiss"
              >
                Reload review
              </Button>
            </div>
          </div>
        );
      })()}

      {/* scene grid — v6.35.34: optional "hide committed" filter so the
          90+ green-state rows don't drown out the small number of rows the
          operator actually needs to act on. Defaults ON. */}
      <div className="flex items-center justify-between gap-2 text-xs text-gray-600">
        <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid="bp-visual-fix-hide-committed-label">
          <input
            type="checkbox"
            checked={hideCommitted}
            onChange={(e) => setHideCommitted(e.target.checked)}
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            data-testid="bp-visual-fix-hide-committed-toggle"
          />
          <span>Hide committed scenes ({counts.committed || 0})</span>
        </label>
        <span className="text-gray-400">
          Showing {scenes.filter(s => !hideCommitted || s.status !== 'committed').length} of {scenes.length}
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {scenes
          .filter(s => !hideCommitted || s.status !== 'committed')
          .map((s, i) => (
          <div
            key={`${s.grade}|${s.context}|${i}`}
            className={`border rounded-lg p-3 ${s.status === 'pending' ? 'border-amber-200 bg-amber-50/30' : 'border-gray-200 bg-white'}`}
            data-testid={`bp-visual-fix-row-${i}`}
          >
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Badge variant="outline" className="bg-white">Gr {s.grade}</Badge>
              <span className="text-sm font-semibold truncate flex-1 min-w-0">{s.context}</span>
              <Badge className={`border ${statusStyle(s.status)}`} variant="outline">{s.status}</Badge>
              <Badge variant="outline" className="bg-white text-gray-600">{s.question_count || 0} Qs</Badge>
            </div>

            {/* Missing-anchor pills so reviewers know what the new image must show */}
            {(s.missing_anchors || []).length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1">
                {(s.missing_anchors || []).slice(0, 6).map((a, j) => (
                  <span
                    key={j}
                    className="inline-flex items-center gap-1 text-[10px] bg-indigo-50 text-indigo-800 border border-indigo-200 rounded px-1.5 py-0.5"
                    title={a.evidence || ''}
                  >
                    <Target size={9} /> {a.correct_answer?.slice(0, 38) || 'anchor'}
                  </span>
                ))}
                {(s.missing_anchors || []).length > 6 && (
                  <span className="text-[10px] text-gray-500">+{(s.missing_anchors || []).length - 6} more</span>
                )}
              </div>
            )}

            {s.error ? (
              <div className="space-y-2">
                <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
                  Generation error: {s.error}
                </div>
                <div className="flex items-center justify-end gap-2">
                  {/* v6.35.34: dismiss STALE/historical errors (e.g. content-router
                      budget-cap strings from before direct-content-engine cutover) so
                      operators can clear UI cruft without re-running the
                      whole batch. Audit-logged on the backend. */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => dismissError(s)}
                    disabled={dismissingKey === `${s.grade}|${s.context}`}
                    className="border-gray-300 text-gray-700 hover:bg-gray-50"
                    data-testid={`bp-visual-fix-dismiss-error-${i}`}
                    title="Clear the stored error string. Does NOT touch the live student image."
                  >
                    {dismissingKey === `${s.grade}|${s.context}`
                      ? <><Loader2 size={12} className="mr-1 animate-spin" /> Dismissing…</>
                      : <><Ban size={12} className="mr-1" /> Dismiss</>}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => retryScene(s)}
                    disabled={retryingKey === `${s.grade}|${s.context}`}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                    data-testid={`bp-visual-fix-retry-${i}`}
                  >
                    {retryingKey === `${s.grade}|${s.context}`
                      ? <><Loader2 size={12} className="mr-1 animate-spin" /> Retrying…</>
                      : <><Wand2 size={12} className="mr-1" /> Retry generation</>}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-[10px] uppercase text-gray-500 mb-1">Current (anchor miss)</div>
                  {s.current_url
                    ? <img src={s.current_url} alt="current" className="w-full h-32 object-cover border rounded" />
                    : <div className="w-full h-32 border rounded bg-gray-50 flex items-center justify-center text-xs text-gray-400">no image</div>}
                </div>
                <div>
                  <div className="text-[10px] uppercase text-emerald-700 mb-1">Candidate (anchors present)</div>
                  {s.candidate_url
                    ? <img src={s.candidate_url} alt="candidate" className="w-full h-32 object-cover border border-emerald-300 rounded" />
                    : <div className="w-full h-32 border rounded bg-gray-50 flex items-center justify-center text-xs text-gray-400">n/a</div>}
                </div>
              </div>
            )}

            <div className="flex items-center justify-end gap-2 mt-2">
              {s.status === 'pending' && (
                <>
                  <Button size="sm" variant="outline" onClick={() => rejectScene(s)} data-testid={`bp-visual-fix-reject-${i}`}>
                    <Ban size={12} className="mr-1" /> Reject
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => openApproveAll([{ grade: s.grade, context: s.context }])}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid={`bp-visual-fix-approve-${i}`}
                  >
                    <CheckCircle2 size={12} className="mr-1" /> Approve
                  </Button>
                </>
              )}
              {s.status === 'rejected' && (
                <Button size="sm" variant="outline" onClick={() => unrejectScene(s)} data-testid={`bp-visual-fix-restore-${i}`}>
                  <RefreshCw size={12} className="mr-1" /> Restore
                </Button>
              )}
              {s.status === 'committed' && (
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 border text-[11px]" variant="outline">
                  <CheckCircle2 size={11} className="mr-1" /> Live in bp_images/
                </Badge>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* password dialog */}
      <Dialog open={pwOpen} onOpenChange={(v) => !v && setPwOpen(false)}>
        <DialogContent data-testid="bp-visual-fix-approve-dialog">
          <DialogHeader>
            <DialogTitle>Confirm Visual-Anchor Bulk Commit</DialogTitle>
            <DialogDescription>
              {selectedOnly
                ? `Commit ${selectedOnly.length} approved visual-fix candidate${selectedOnly.length === 1 ? '' : 's'} to production.`
                : `Commit all ${counts.pending || 0} pending visual-fix candidate${counts.pending === 1 ? '' : 's'} to production. Rejected scenes will be skipped.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900 space-y-1">
              <div><strong>Production live.</strong> Each approval copies the candidate to
              <code className="bg-white px-1 mx-1 rounded">/bp_images/</code>
              and updates every question bound to that scene. A rollback manifest is written per scene
              + a bundle ID (prefix <code>bpvis_</code>) for single-command bulk revert.</div>
              <div className="pt-1 border-t border-amber-200">
                <strong>Press-PDF auto-regen.</strong> After commit, all 112 print-ready BLM booklet PDFs will be regenerated in the background (~2-3 min) so print files pick up the new images automatically. No separate click needed.
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">Super Admin password</label>
              <Input
                type="password"
                value={pw}
                onChange={e => setPw(e.target.value)}
                autoFocus
                onKeyDown={e => e.key === 'Enter' && !approving && confirmApprove()}
                data-testid="bp-visual-fix-password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPwOpen(false)} disabled={approving}>
              <XCircle size={14} className="mr-1" /> Cancel
            </Button>
            <Button
              onClick={confirmApprove}
              disabled={approving || !pw}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="bp-visual-fix-confirm-approve"
            >
              {approving
                ? <><Loader2 size={14} className="mr-1 animate-spin" /> Committing…</>
                : <><ShieldCheck size={14} className="mr-1" /> Commit</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v6.32.15: regen-all confirmation dialog */}
      <Dialog open={regenPwOpen} onOpenChange={(v) => !v && setRegenPwOpen(false)}>
        <DialogContent data-testid="bp-visual-fix-regen-dialog">
          <DialogHeader>
            <DialogTitle>Regenerate all visual-fix candidates</DialogTitle>
            <DialogDescription>
              Generates a fresh candidate for every scene that overall-failed
              without text violations. Replaces the current review payload.
              Takes ~8-12 min for 40 scenes. Runs in the background; this panel
              auto-refreshes when done.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900">
            <strong>content-engine cost</strong>: ~$1-2 per run (MOSAIC engine image generation for all flagged scenes).
            <br />
            <strong>Multi-pod safe</strong>: each candidate is stored in MongoDB so any backend replica can serve it on prod.
          </div>
          <Input
            type="password"
            placeholder="Super Admin password"
            value={regenPw}
            onChange={e => setRegenPw(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !regenStarting && startRegen()}
            autoFocus
            data-testid="bp-visual-fix-regen-password"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegenPwOpen(false)} disabled={regenStarting}>
              Cancel
            </Button>
            <Button
              onClick={startRegen}
              disabled={regenStarting || !regenPw}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="bp-visual-fix-regen-confirm"
            >
              {regenStarting
                ? <><Loader2 size={14} className="mr-1 animate-spin" />Starting…</>
                : 'Start regeneration'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* v6.32.16: cancel-run confirmation dialog */}
      <Dialog open={cancelPwOpen} onOpenChange={(v) => !v && setCancelPwOpen(false)}>
        <DialogContent data-testid="bp-visual-fix-cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancel the running regeneration?</DialogTitle>
            <DialogDescription>
              Marks the current visual-fix regen as <code>cancelled</code> so
              the progress banner clears and you can click Regenerate all
              again. Candidate blobs that were already persisted to MongoDB
              before the worker died are kept (TTL auto-purge in 30 days),
              but your Approve-All flow only operates on the <em>review
              payload</em>, which only lands at the end of a successful run —
              so there's nothing lingering to clean up.
            </DialogDescription>
          </DialogHeader>
          <Input
            type="password"
            placeholder="Super Admin password"
            value={cancelPw}
            onChange={e => setCancelPw(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !cancelling && cancelRun()}
            autoFocus
            data-testid="bp-visual-fix-cancel-password"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelPwOpen(false)} disabled={cancelling}>
              Keep running
            </Button>
            <Button
              onClick={cancelRun}
              disabled={cancelling || !cancelPw}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="bp-visual-fix-cancel-confirm"
            >
              {cancelling
                ? <><Loader2 size={14} className="mr-1 animate-spin" />Cancelling…</>
                : 'Cancel run'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BPVisualFixBatchPanel;
